import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LensIcon from '@mui/icons-material/Lens';
import {
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import React, { type FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCheckPermissions } from '../../auth/index.js';
import { type Patient } from '../../models/index.js';

const MEDIA_PROVIDER_CHIP: Record<'antmedia' | 'mediamtx', { label: string; name: string }> = {
  antmedia: { label: 'AMS', name: 'Ant Media' },
  mediamtx: { label: 'MTX', name: 'MediaMTX' },
};

interface PatientsListProps {
  patients: Patient[];
  searchValue: string;
}
const PatientsList: FC<PatientsListProps> = ({ patients, searchValue }) => {
  const navigate = useNavigate();
  const { canReadActivityEvent } = useCheckPermissions();

  return (
    <List sx={{ width: '100%', maxWidth: 360, minWidth: 200 }}>
      {patients?.map((patient) => {
        const matches = match(`${patient.firstName} ${patient.lastName}`, searchValue, { insideWords: true });
        const parts: Array<{ text: string; highlight: boolean }> = parse(
          `${patient.firstName} ${patient.lastName}`,
          matches,
        );
        return (
          <ListItem key={patient.id} disablePadding>
            <ListItemButton onClick={() => navigate(`/patients/${patient.id}`)}>
              <ListItemText>
                {parts.map((part, index) => (
                  <Typography
                    key={`${part.text}-${index}`}
                    sx={{
                      display: 'inline',
                      fontWeight: part.highlight ? 700 : 400,
                    }}
                  >
                    {part.text}
                  </Typography>
                ))}
              </ListItemText>
              <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
                {canReadActivityEvent && patient.activityRecognitionEnabled ? (
                  <Tooltip arrow title={'AI monitoring enabled'}>
                    <AutoAwesomeIcon color={'primary'} fontSize={'small'} />
                  </Tooltip>
                ) : null}
                {patient.mediaProvider ? (
                  <Tooltip arrow title={`Streaming via ${MEDIA_PROVIDER_CHIP[patient.mediaProvider].name}`}>
                    <Chip
                      size={'small'}
                      variant={'outlined'}
                      label={MEDIA_PROVIDER_CHIP[patient.mediaProvider].label}
                      sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                    />
                  </Tooltip>
                ) : null}
                <ListItemIcon
                  sx={{
                    minWidth: 'initial',
                  }}
                >
                  <LensIcon color={patient.inMonitoringHours ? 'success' : 'error'} />
                </ListItemIcon>
              </Stack>
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};

export default PatientsList;
